import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Modal } from '@/components/Modal';
import { AccountStatusBadge } from '@/features/access/AccountStatusBadge';
import { NameLoginDialog } from '@/features/access/NameLoginDialog';
import { UserFormFields, useCreateUser } from '@/features/access/UserForm';
import { ExportSheetButton, ImportSheetDialog } from '@/features/sheets/ExcelSheet';
import { SendDocumentsDialog } from '@/features/documents/SendDocumentsDialog';
import { FolderUploadDialog } from '@/features/documents/FolderUploadDialog';
import { usePermissions } from '@/features/access/usePermissions';
import { DoorChip } from '@/features/access/UsersAccess';
import { MobileList } from '@/components/MobileList';
import { DataToolbar, NoResults, SortableTh } from '@/components/table';
import { Avatar, Badge, Card, ErrorNotice, PageHeader, Spinner } from '@/components/ui';
import { useI18n } from '@/i18n';
import { api, ApiError, type User } from '@/lib/api';
import { dateShort, fullName } from '@/lib/format';
import { useTableState, type SortOption } from '@/lib/table';
import { useLabel } from '@/lib/useLabel';

const SORTS: SortOption<User>[] = [
  { key: 'name', labelKey: 'employees.name', value: (u) => `${u.lastName} ${u.firstName}` },
  { key: 'role', labelKey: 'employees.role', value: (u) => u.role },
  { key: 'status', labelKey: 'common.status', value: (u) => u.status },
  { key: 'created', labelKey: 'employees.createdAt', value: (u) => u.createdAt, defaultDirection: 'desc' },
];

export function EmployeesPage() {
  const { t } = useI18n();
  const L = useLabel();
  const [creating, setCreating] = useState(false);
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.users() });

  // Role and status are searchable by their *translated* words, so typing "tecnico"
  // finds engineers in Italian and "engineer" finds them in English. Searching the raw
  // enum would mean searching for a word nobody sees on screen.
  const { can } = usePermissions();

  const table = useTableState<User>({
    rows: users.data?.users ?? [],
    search: (u) => [
      u.firstName,
      u.lastName,
      u.email ?? '',
      u.phone,
      L('role', u.role).label,
      L('user', u.status).label,
    ],
    sorts: SORTS,
    defaultSortKey: 'name',
    storageKey: 'employees',
  });

  // Someone added with no email has no way in yet: offer the name login straight away.
  const [justAdded, setJustAdded] = useState<User | null>(null);
  const { form, setForm, create, valid: canCreate } = useCreateUser((created) => {
    setCreating(false);
    if (!created.email && created.role !== 'admin' && can('users', 'write')) setJustAdded(created);
  });
  const [importing, setImporting] = useState(false);

  // Ticked people, for "Send documents": who goes to the customer is picked here, in
  // the list the office already searches and sorts, not again inside the dialog.
  const canSend = can('compliance', 'write');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [uploadingFolders, setUploadingFolders] = useState(false);
  const toggle = (id: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allShown = table.rows.length > 0 && table.rows.every((u) => picked.has(u.id));
  const toggleAll = () =>
    setPicked((prev) => {
      const next = new Set(prev);
      for (const u of table.rows) {
        if (allShown) next.delete(u.id);
        else next.add(u.id);
      }
      return next;
    });

  return (
    <>
      <PageHeader
        title={t('employees.title')}
        subtitle={t('employees.subtitle')}
        actions={
          <>
            {canSend ? (
              <button type="button" className="btn-secondary" onClick={() => setUploadingFolders(true)}>
                {t('bulk.button')}
              </button>
            ) : null}
            {canSend ? (
              <button type="button" className="btn-secondary" onClick={() => setSending(true)}>
                {picked.size > 0 ? t('send.buttonCount', { count: picked.size }) : t('send.button')}
              </button>
            ) : null}
            <ExportSheetButton kind="employees" />
            {can('employees', 'write') ? (
              <button type="button" className="btn-secondary" onClick={() => setImporting(true)}>
                {t('sheet.import')}
              </button>
            ) : null}
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              {t('employees.create')}
            </button>
          </>
        }
      />

      <ImportSheetDialog kind="employees" open={importing} onClose={() => setImporting(false)} />
      <SendDocumentsDialog open={sending} onClose={() => setSending(false)} initialUserIds={[...picked]} />
      <FolderUploadDialog open={uploadingFolders} onClose={() => setUploadingFolders(false)} />

      {justAdded ? (
        <NameLoginDialog
          person={justAdded}
          mode="create"
          open
          onClose={() => setJustAdded(null)}
          intro={t('access.nameLogin.offerAfterAdd', { name: fullName(justAdded) })}
        />
      ) : null}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title={t('employees.create')}
        description={t('employees.createHint')}
        footer={
          <>
            <button
              type="button"
              className="btn-primary"
              disabled={!canCreate}
              onClick={() => create.mutate()}
            >
              {create.isPending
                ? t('common.saving')
                : form.email.trim()
                  ? t('employees.createAccount')
                  : t('employees.addOnly')}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setCreating(false)}>
              {t('common.cancel')}
            </button>

            {create.error ? (
              <p className="text-sm text-danger ms-auto">
                {create.error instanceof ApiError ? create.error.message : t('common.saveError')}
              </p>
            ) : null}
          </>
        }
      >
        <UserFormFields form={form} setForm={setForm} onSubmit={() => canCreate && create.mutate()} />
      </Modal>

      {users.isLoading ? (
        <Spinner />
      ) : users.error ? (
        <ErrorNotice error={users.error} />
      ) : (
        <>
          <DataToolbar
            state={table}
            searchPlaceholder={t('employees.searchPlaceholder')}
            countOne="employees.countOne"
            countMany="employees.countMany"
          />

          {picked.size > 0 ? (
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded border border-border bg-surface-alt px-3 py-2 text-sm">
              <span className="font-semibold">{t('send.selectedCount', { count: picked.size })}</span>
              <button type="button" className="btn-primary h-8" onClick={() => setSending(true)}>
                {t('send.button')}
              </button>
              <button type="button" className="text-muted hover:text-text hover:underline" onClick={() => setPicked(new Set())}>
                {t('send.clearSelection')}
              </button>
            </div>
          ) : null}

          <Card padded={false}>
            {table.rows.length === 0 ? (
              <NoResults onClear={table.clear} />
            ) : (
              <>
                <div className="lg:hidden p-2">
                  <MobileList<User>
                    rows={table.rows}
                    keyOf={(u) => u.id}
                    title={(u) => fullName(u)}
                    subtitle={(u) => u.email ?? t('access.noEmail')}
                    badges={(u) => (
                      <>
                        <Badge tone={L('role', u.role).tone}>{L('role', u.role).label}</Badge>
                        <DoorChip on={u.portalAccess} label={t('access.doorPortalShort')} />
                        <DoorChip on={u.appAccess} label={t('access.doorAppShort')} />
                        <AccountStatusBadge user={u} />
                      </>
                    )}
                    fields={[
                      { label: t('employees.email'), value: (u) => u.email },
                      { label: t('employees.createdAt'), value: (u) => dateShort(u.createdAt) },
                    ]}
                    action={(u) => (
                      <Link to={`/employees/${u.id}`} className="btn-primary">
                        {t('common.open')}
                      </Link>
                    )}
                  />
                </div>

                <div className="table-scroll max-lg:hidden">
                <table className="table-wide">
                  <thead>
                    <tr>
                      {canSend ? (
                        <th className="th w-10">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[var(--color-primary)]"
                            aria-label={t('send.selectAll')}
                            title={t('send.selectAll')}
                            checked={allShown}
                            onChange={toggleAll}
                          />
                        </th>
                      ) : null}
                      <SortableTh state={table} sortKey="name">
                        {t('employees.name')}
                      </SortableTh>
                      <th className="th">{t('employees.email')}</th>
                      <SortableTh state={table} sortKey="role">
                        {t('employees.role')}
                      </SortableTh>
                      <th className="th">{t('access.signsInTo')}</th>
                      <SortableTh state={table} sortKey="status">
                        {t('common.status')}
                      </SortableTh>
                      <SortableTh state={table} sortKey="created">
                        {t('employees.createdAt')}
                      </SortableTh>
                      <th className="th" />
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((u) => {
                      const role = L('role', u.role);

                      return (
                        <tr key={u.id} className={picked.has(u.id) ? 'bg-primary-soft' : 'hover:bg-surface-alt'}>
                          {canSend ? (
                            <td className="td w-10">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-[var(--color-primary)]"
                                aria-label={t('send.select', { name: fullName(u) })}
                                checked={picked.has(u.id)}
                                onChange={() => toggle(u.id)}
                              />
                            </td>
                          ) : null}
                          <td className="td">
                            <Link
                              to={`/employees/${u.id}`}
                              className="flex items-center gap-2.5 font-semibold hover:text-primary"
                            >
                              <Avatar name={fullName(u)} size={28} photoUrl={u.photoUrl} />
                              {fullName(u)}
                            </Link>
                          </td>
                          <td className="td text-muted">{u.email ?? <span className="text-subtle">—</span>}</td>
                          <td className="td">
                            <Badge tone={role.tone}>{role.label}</Badge>
                          </td>
                          <td className="td">
                            <span className="inline-flex gap-1.5">
                              <DoorChip on={u.portalAccess} label={t('access.doorPortalShort')} />
                              <DoorChip on={u.appAccess} label={t('access.doorAppShort')} />
                            </span>
                          </td>
                          <td className="td">
                            <AccountStatusBadge user={u} />
                          </td>
                          <td className="td text-muted tabular whitespace-nowrap">
                            {dateShort(u.createdAt)}
                          </td>
                          <td className="td text-end">
                            <Link
                              to={`/employees/${u.id}`}
                              className="td-action text-primary hover:underline"
                            >
                              {t('common.open')}
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </Card>
        </>
      )}
    </>
  );
}
